// The Accounts screen in /admin: every login that opens a UMaps tool, in one
// place, with the ones we can change actually changeable.
//
// Two kinds of account appear here and the difference is the whole design:
//
//  · STORED accounts live in KV. Create, rename, re-password and delete them
//    here; a change takes effect on the next login, no redeploy.
//  · ENVIRONMENT accounts come from TOUR_EDITORS / BUILDING_EDITORS /
//    EVENT_EDITORS. They are listed because they work — a screen that showed
//    only half the working logins would be worse than none — but they are
//    read-only, because a web page cannot rewrite Vercel's environment. Each one
//    says which variable it came from, so removing it is a known job rather than
//    a mystery.
//
// The operator's own account is shown last and cannot be touched from here. It
// is the credential that opens this page, and there is no second way in.
//
// ⚠️ A PASSWORD IS READABLE EXACTLY ONCE — in the response to the call that set
// it (create, reset), which lands in the "to send" panel at the top of this
// screen. Nothing stores a readable password, so there is no list to come back
// to and no "reveal" button anywhere: an operator who has lost one issues a new
// one, which is one tap and is also the correct answer to somebody who has lost
// it.
//
// ⚠️ CLUB LOGINS ARE NOT HERE. They live on /admin → Events Admin, beside the
// events they publish, because a club account is not the same kind of object as
// an internal tool login: clubs register themselves, choose their own password,
// and have to be approved before anything they make is public. Managing them
// next to "the Asper school needs a Building Directories login" made both jobs
// read as the same job. This screen is now only the logins WE issue.

import { useCallback, useEffect, useMemo, useState } from 'react';

// Events are deliberately absent — see the note above.
const TOOL_ORDER = ['buildings', 'tour', 'hunt'];
const TOOL_META = {
  buildings: { icon: '🔳', label: 'Building Directories', variable: 'BUILDING_EDITORS',
               desc: 'Place and print QR signage for a building.' },
  tour: { icon: '🧭', label: 'Eng Tour Creator', variable: 'TOUR_EDITORS',
          desc: 'Build and edit the engineering tour. Also opens Building Directories.' },
  hunt: { icon: '🏁', label: 'Scavenger Hunt Admin', variable: '',
          desc: 'Judge the NBSC scavenger hunt at /NBSCScavengerhuntadmin: teams, stamps, timer, photo review.' },
};

const fmt = (ts) => { try { return ts ? new Date(ts).toLocaleDateString() : ''; } catch { return ''; } };

export default function AccountsManager({ apiBase, token, onUnauthorized }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [editing, setEditing] = useState(null);   // account id being edited
  const [confirming, setConfirming] = useState(null); // account id pending delete
  const [creatingFor, setCreatingFor] = useState(null); // tool key of the open form

  const call = useCallback(async (method, body) => {
    const r = await fetch(`${apiBase}/api/accounts`, {
      method,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    if (r.status === 401 || r.status === 403) { onUnauthorized?.(); throw new Error('Not allowed — sign in as the admin.'); }
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || 'That did not go through.');
    return j;
  }, [apiBase, token, onUnauthorized]);

  const load = useCallback(async () => {
    setBusy(true); setErr('');
    try { setData(await call('GET')); }
    catch (e) { setErr(e.message); }
    setBusy(false);
  }, [call]);

  useEffect(() => { load(); }, [load]);

  // Credentials issued in this sitting, waiting to be sent. Held in component
  // state on purpose: they are gone on refresh, which is the correct lifetime
  // for the only readable copy of a password that exists anywhere.
  const [issued, setIssued] = useState([]);

  const act = async (body, okMsg) => {
    setBusy(true); setErr(''); setMsg('');
    let out = null;
    try {
      out = await call('POST', body);
      setMsg(okMsg);
      setEditing(null); setConfirming(null); setCreatingFor(null);
      await load();
    } catch (e) { setErr(e.message); }
    setBusy(false);
    return out;
  };

  // Anything that mints a password funnels its result here, so the panel is the
  // single place a new login is ever shown — and one place to get right.
  const collect = (rows) => {
    const fresh = (rows || []).filter(r => r?.password);
    if (fresh.length) setIssued(prev => [...fresh, ...prev]);
  };

  // One list per tool: stored accounts first (they're the actionable ones),
  // then the environment's, which are read-only.
  const byTool = useMemo(() => {
    const stored = data?.accounts || [];
    const env = data?.env || {};
    return TOOL_ORDER.map(tool => ({
      tool,
      meta: TOOL_META[tool],
      stored: stored.filter(a => a.tool === tool),
      env: (env[tool] || []).map(name => ({ username: name, source: 'env' })),
    }));
  }, [data]);

  const total = (data?.accounts?.length || 0)
    + TOOL_ORDER.reduce((n, t) => n + (data?.env?.[t]?.length || 0), 0);

  return (
    <div>
      <div style={S.intro}>
        <div>
          <b>{total}</b> login{total === 1 ? '' : 's'} across {TOOL_ORDER.length} tools.
          Accounts you create here work on the next login — no redeploy.
        </div>
        <button style={S.ghost} onClick={load} disabled={busy}>{busy ? 'Loading…' : 'Refresh'}</button>
      </div>

      {err && <div style={S.err}>{err}</div>}
      {msg && <div style={S.ok}>{msg}</div>}

      {issued.length > 0 && (
        <IssuedPanel rows={issued} onClear={() => setIssued([])} />
      )}

      {byTool.map(({ tool, meta, stored, env }) => (
        <div key={tool} style={S.card}>
          <div style={S.cardHead}>
            <span style={S.cardIcon}>{meta.icon}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={S.cardName}>{meta.label}</div>
              <div style={S.cardDesc}>{meta.desc}</div>
            </div>
            <button style={S.primary}
                    onClick={() => { setCreatingFor(creatingFor === tool ? null : tool); setErr(''); setMsg(''); }}>
              {creatingFor === tool ? 'Cancel' : '+ Add account'}
            </button>
          </div>

          {creatingFor === tool && (
            <CreateForm tool={tool} busy={busy}
                        onCancel={() => setCreatingFor(null)}
                        onCreate={async (body) => {
                          const out = await act({ op: 'create', ...body }, `Created ${body.username}.`);
                          // Only a GENERATED password comes back — one typed in
                          // the form is already known to whoever typed it, and
                          // echoing it into the "to send" panel would be noise.
                          if (out?.account?.password && !body.password) {
                            collect([{ username: out.account.username, password: out.account.password }]);
                          }
                        }} />
          )}

          {stored.length === 0 && env.length === 0 && (
            <div style={S.empty}>No logins for this tool yet.</div>
          )}

          {stored.map(a => (
            <div key={a.id} style={S.row}>
              {editing === a.id ? (
                <EditForm account={a} busy={busy}
                          onCancel={() => setEditing(null)}
                          onSave={(body) => act({ op: 'update', id: a.id, ...body }, `Saved ${body.username || a.username}.`)} />
              ) : confirming === a.id ? (
                <div style={S.confirm}>
                  <span>Delete <b>{a.username}</b>? They will not be able to sign in.</span>
                  <div style={S.rowActions}>
                    <button style={S.ghost} onClick={() => setConfirming(null)} disabled={busy}>Keep</button>
                    <button style={S.danger} disabled={busy}
                            onClick={() => act({ op: 'delete', id: a.id }, `Deleted ${a.username}.`)}>
                      Delete
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={S.user}>
                      {a.username}
                      {/* Created but with no password yet. Rare here — these are
                          logins we hand over ourselves — but the state exists
                          for every account, so a row that is in it must say so
                          rather than look like a working login that isn't. */}
                      {a.pending && <span style={S.pending}>Set up</span>}
                    </div>
                    <div style={S.meta}>
                      {a.email ? <span style={S.dim}>{a.email} · </span> : null}
                      {a.note ? `${a.note} · ` : ''}
                      added {fmt(a.createdAt)}
                      {a.updatedAt && a.updatedAt !== a.createdAt ? ` · changed ${fmt(a.updatedAt)}` : ''}
                    </div>
                  </div>
                  <div style={S.rowActions}>
                    {/* Not "show password" — there is nothing to show. This
                        issues a new one and puts it in the panel above, which
                        answers both "they lost it" and "I never sent it". */}
                    <button style={S.ghost} disabled={busy}
                            title="Issue a new password and show it once, so you can send it"
                            onClick={async () => {
                              const out = await act({ op: 'reset-password', id: a.id }, `New password for ${a.username}.`);
                              if (out?.account) {
                                collect([{ username: out.account.username, password: out.account.password }]);
                              }
                            }}>New password</button>
                    <button style={S.ghost} onClick={() => { setEditing(a.id); setErr(''); }}>Edit</button>
                    <button style={S.dangerGhost} onClick={() => { setConfirming(a.id); setErr(''); }}>Delete</button>
                  </div>
                </>
              )}
            </div>
          ))}

          {env.map(a => (
            <div key={`env-${a.username}`} style={{ ...S.row, ...S.rowEnv }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.user}>{a.username}</div>
                <div style={S.meta}>
                  From the <code style={S.code}>{meta.variable}</code> environment variable
                </div>
              </div>
              <span style={S.badge} title="Change this in Vercel → Settings → Environment Variables, then redeploy">
                Read-only
              </span>
            </div>
          ))}
        </div>
      ))}

      {/* The operator, listed for completeness and deliberately inert. */}
      <div style={S.card}>
        <div style={S.cardHead}>
          <span style={S.cardIcon}>🔑</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={S.cardName}>UMaps Admin</div>
            <div style={S.cardDesc}>Opens every tool, including this screen.</div>
          </div>
        </div>
        <div style={{ ...S.row, ...S.rowEnv }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={S.user}>{data?.admin?.username || 'admin'}</div>
            <div style={S.meta}>
              From <code style={S.code}>ADMIN_USER</code> / <code style={S.code}>ADMIN_PASSWORD</code>
              {data && !data.admin?.configured && ' — not set yet, so the tools still accept the old shared password'}
            </div>
          </div>
          <span style={S.badge} title="Not editable here on purpose: this is the credential that opens this page">
            Read-only
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * The logins minted in this sitting, with their passwords, waiting to be sent.
 *
 * This panel is the ONLY place a password is ever readable, and it lasts until
 * the page is reloaded — so it leads with what that means rather than burying
 * it, and it carries the copy buttons that make "send sixty emails" a job
 * somebody finishes. Per row: the credentials alone, or the whole welcome
 * message; for the batch: every row as one block, to paste into a spreadsheet
 * or a mail merge.
 *
 * A row whose club has no contact email says so instead of pretending it can be
 * sent — that is the field the operator has to fill in first, and it is the
 * commonest state on a freshly seeded club.
 */
function IssuedPanel({ rows, onClear }) {
  const [copied, setCopied] = useState('');
  // The site, not a tool path: what lands here is a login for any of three
  // tools, and naming the wrong one is worse than naming none.
  const url = typeof window !== 'undefined' ? window.location.origin : '';

  const copy = async (text, key) => {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
      else {
        const ta = document.createElement('textarea');
        ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select(); document.execCommand('copy');
        document.body.removeChild(ta);
      }
    } catch { /* the text is on screen either way */ }
    setCopied(key);
    setTimeout(() => setCopied(c => (c === key ? '' : c)), 1800);
  };

  // Clubs never reach this panel — they set their own password from a link, so
  // there is nothing here to send them. What lands here is an internal tool
  // login, which is why the message names neither a club nor a tool.
  const welcome = (r) => [
    `Hi ${r.displayName || r.username},`,
    '',
    'Here are your UMaps sign-in details.',
    '',
    `Sign in at: ${url}`,
    `Username: ${r.username}`,
    `Password: ${r.password}`,
    '',
    'Please change the password after your first sign-in.',
    '',
    '— UMaps',
  ].join('\n');

  const allRows = rows.map(r => `${r.displayName || r.username}\t${r.email || ''}\t${r.username}\t${r.password}`).join('\n');

  return (
    <div style={S.issued}>
      <div style={S.issuedHead}>
        <div>
          <div style={S.issuedTitle}>{rows.length} login{rows.length === 1 ? '' : 's'} to send</div>
          <div style={S.issuedSub}>
            Shown once. Reload this page and these passwords are gone for good — nothing
            stores them. If you lose one, “New password” issues another.
          </div>
        </div>
        <div style={S.rowActions}>
          <button style={S.ghost} onClick={() => copy(allRows, 'all')}>
            {copied === 'all' ? 'Copied' : 'Copy all as a table'}
          </button>
          <button style={S.primary} onClick={onClear}>Done — clear</button>
        </div>
      </div>
      {rows.map((r, i) => (
        <div key={`${r.username}-${i}`} style={S.issuedRow}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={S.user}>{r.displayName || r.username}</div>
            <div style={S.meta}>
              {r.email ? <span style={S.email}>{r.email}</span> : <span style={S.todo}>no contact email — add one before sending</span>}
            </div>
          </div>
          <code style={S.cred}>{r.username}</code>
          <code style={S.credPw}>{r.password}</code>
          <div style={S.rowActions}>
            <button style={S.ghost} onClick={() => copy(`${r.username} / ${r.password}`, `c${i}`)}>
              {copied === `c${i}` ? 'Copied' : 'Copy'}
            </button>
            <button style={S.ghost} onClick={() => copy(welcome(r), `w${i}`)}>
              {copied === `w${i}` ? 'Copied' : 'Copy email'}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function CreateForm({ tool, busy, onCancel, onCreate }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [note, setNote] = useState('');
  // A blank password is not an incomplete form — it means "generate one", which
  // is what you want when the login is for somebody else.
  const ok = username.trim() && (password === '' || password.length >= 8);
  return (
    <div style={S.form}>
      <div style={S.formRow}>
        <input style={S.input} autoFocus placeholder="Username, e.g. AsperSchoolOfBusiness"
               value={username} onChange={e => setUsername(e.target.value)} />
        <input style={S.input} type="text" placeholder="Password — leave blank to generate one"
               value={password} onChange={e => setPassword(e.target.value)} />
      </div>
      <input style={{ ...S.input, width: '100%', boxSizing: 'border-box' }}
             placeholder="Note (optional) — who this is for"
             value={note} onChange={e => setNote(e.target.value)} />
      <div style={S.formHint}>
        Letters, numbers, dot, dash or underscore — no spaces. Leave the password blank
        and one is generated for you and shown once at the top of this screen, ready to
        send. Only a hash is stored either way, so it cannot be looked up later.
      </div>
      <div style={S.rowActions}>
        <button style={S.ghost} onClick={onCancel} disabled={busy}>Cancel</button>
        <button style={S.primary} disabled={!ok || busy}
                onClick={() => onCreate({
                  tool, username: username.trim(), note: note.trim(),
                  ...(password ? { password } : {}),
                })}>
          {busy ? 'Saving…' : 'Create account'}
        </button>
      </div>
    </div>
  );
}

function EditForm({ account, busy, onCancel, onSave }) {
  const [username, setUsername] = useState(account.username);
  const [password, setPassword] = useState('');
  const [note, setNote] = useState(account.note || '');
  const changed = username.trim() !== account.username || password !== '' || note !== (account.note || '');
  const pwOk = password === '' || password.length >= 8;
  return (
    <div style={S.form}>
      <div style={S.formRow}>
        <input style={S.input} autoFocus value={username} onChange={e => setUsername(e.target.value)} />
        <input style={S.input} type="text" placeholder="New password (leave blank to keep)"
               value={password} onChange={e => setPassword(e.target.value)} />
      </div>
      <input style={{ ...S.input, width: '100%', boxSizing: 'border-box' }}
             placeholder="Note (optional)" value={note} onChange={e => setNote(e.target.value)} />
      <div style={S.formHint}>
        Leave the password blank to keep the current one. There is nothing to reveal —
        only a hash is stored.
      </div>
      <div style={S.rowActions}>
        <button style={S.ghost} onClick={onCancel} disabled={busy}>Cancel</button>
        <button style={S.primary} disabled={!changed || !pwOk || busy}
                onClick={() => onSave({
                  username: username.trim(),
                  ...(password ? { password } : {}),
                  note: note.trim(),
                })}>
          {busy ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

const S = {
  intro: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', fontSize: 13.5, color: '#3c4043', marginBottom: 12 },
  card: { background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', marginBottom: 14, overflow: 'hidden' },
  cardHead: { display: 'flex', alignItems: 'center', gap: 12, padding: '13px 14px', borderBottom: '1px solid #f1f3f4' },
  cardIcon: { fontSize: 20, flexShrink: 0 },
  cardName: { fontSize: 14.5, fontWeight: 800, color: '#202124' },
  cardDesc: { fontSize: 12, color: '#5f6368', marginTop: 1 },
  row: { display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', borderBottom: '1px solid #f6f7f8' },
  rowEnv: { background: '#fafbfc' },
  rowActions: { display: 'flex', gap: 6, flexShrink: 0 },
  user: { fontSize: 13.5, fontWeight: 700, color: '#202124', wordBreak: 'break-all' },
  meta: { fontSize: 11.5, color: '#5f6368', marginTop: 2 },
  code: { background: '#f1f3f4', borderRadius: 4, padding: '1px 5px', fontSize: 11 },
  badge: { flexShrink: 0, background: '#f1f3f4', color: '#5f6368', borderRadius: 999, padding: '4px 10px', fontSize: 11, fontWeight: 700 },
  empty: { padding: '13px 14px', fontSize: 12.5, color: '#5f6368' },
  form: { padding: '13px 14px', background: '#f8fbff', borderBottom: '1px solid #f1f3f4', display: 'flex', flexDirection: 'column', gap: 8 },
  formRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  formHint: { fontSize: 11.5, color: '#5f6368', lineHeight: 1.4 },
  input: { flex: '1 1 200px', minWidth: 0, fontSize: 13.5, padding: '8px 10px', border: '1.5px solid #dadce0', borderRadius: 8, outline: 'none' },
  primary: { background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  ghost: { background: '#fff', color: '#202124', border: '1.5px solid #dadce0', borderRadius: 8, padding: '7px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' },
  danger: { background: '#c5221f', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  dangerGhost: { background: '#fff', color: '#c5221f', border: '1.5px solid #f3c2c0', borderRadius: 8, padding: '7px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' },
  confirm: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', width: '100%', fontSize: 13, color: '#202124' },
  err: { background: '#fce8e6', color: '#c5221f', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginBottom: 12 },
  ok: { background: '#e6f4ea', color: '#137333', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginBottom: 12 },

  // The login shown beside its club, small: the club is what you recognise, the
  // username is what you type.
  userLogin: { marginLeft: 7, fontSize: 11.5, fontWeight: 600, color: '#5f6368', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' },
  email: { color: '#1a56c4', fontWeight: 600 },
  dim: { color: '#80868b' },
  // A club nobody can sign in as. Red, unlike the amber to-dos: an empty logo
  // is work outstanding, this is a login that does not exist.
  blocked: { color: '#c5221f', fontWeight: 700 },
  // A field the operator still has to fill in. Amber rather than red: an empty
  // logo is a to-do, not a fault.
  todo: { color: '#a15c00', fontWeight: 600, marginRight: 8 },
  // "Set up" — created, no password yet. Amber like the to-dos and beside the
  // name rather than in the metadata line, because it is the only state on this
  // screen that decides what the operator does next.
  pending: { marginLeft: 8, background: '#fff3e0', color: '#a15c00', border: '1px solid #f3d9a8', borderRadius: 999, padding: '1px 8px', fontSize: 10.5, fontWeight: 800, letterSpacing: '.02em', verticalAlign: 'middle' },

  // A setup link, readable once and sitting under its row until the page is
  // reloaded.
  linkBox: { marginTop: 8, background: '#fffaf0', border: '1px solid #f5d9a8', borderRadius: 8, padding: '8px 10px' },
  linkNote: { fontSize: 11.5, color: '#8a6414', marginBottom: 6, lineHeight: 1.45 },
  linkRow: { display: 'flex', gap: 8, alignItems: 'center' },
  linkUrl: { flex: 1, minWidth: 0, overflowX: 'auto', whiteSpace: 'nowrap', background: '#fff', border: '1px solid #eadcc0', borderRadius: 6, padding: '6px 8px', fontSize: 11.5, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' },

  // Credentials waiting to be sent. Amber all through, because the panel is
  // temporary by design and should not look like part of the furniture.
  issued: { background: '#fffaf0', border: '1.5px solid #f5d9a8', borderRadius: 12, marginBottom: 14, overflow: 'hidden' },
  issuedHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '12px 14px', borderBottom: '1px solid #f5e3c4' },
  issuedTitle: { fontSize: 14.5, fontWeight: 800, color: '#7a4a00' },
  issuedSub: { fontSize: 11.5, color: '#8a6414', marginTop: 2, maxWidth: 460, lineHeight: 1.45 },
  issuedRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '9px 14px', borderBottom: '1px solid #f7ecd9' },
  cred: { flexShrink: 0, background: '#fff', border: '1px solid #eadcc0', borderRadius: 6, padding: '3px 7px', fontSize: 12, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' },
  credPw: { flexShrink: 0, background: '#fff', border: '1px solid #eadcc0', borderRadius: 6, padding: '3px 7px', fontSize: 12.5, fontWeight: 700, letterSpacing: '0.04em', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' },
};
